import React, { useState, useEffect } from "react";
import { getAllUsers, addUser } from "../services/settings";
import { useNavigate } from "react-router-dom";
import Loader from "../components/UI/loader";
import { ref, getDatabase, get, update } from "firebase/database";
import { v4 } from "uuid";

const CONFIG_ITEMS = [
  {
    key: "tables",
    emoji: "🪑",
    label: "Table Configuration",
    desc: "Add and manage your business tables",
    to: "/table-settings",
    configuredLabel: (n) => `${n} table${n !== 1 ? "s" : ""} configured`,
  },
  {
    key: "staff",
    emoji: "👥",
    label: "Staff & Waiters",
    desc: "Add waiters and manage their PIN access",
    to: "/staff-settings",
    configuredLabel: (n) => `${n} waiter${n !== 1 ? "s" : ""} added`,
  },
  {
    key: "menu",
    emoji: "🍽️",
    label: "Menu & Public Page",
    desc: "Set your logo, business name, and menu",
    to: "/menu-settings",
    configuredLabel: () => "Menu configured",
  },
  {
    key: "link",
    emoji: "📱",
    label: "Public Waitlist Link",
    desc: "Share your live queue link or QR with customers",
    to: "/public-link",
    configuredLabel: () => "Ready to share",
    alwaysDone: true,
  },
];

function Settings() {
  const [user, setUser] = useState();
  const [loading, setLoader] = useState(false);
  const [setupStatus, setSetupStatus] = useState({
    tables: null,
    staff: null,
    menu: null,
    link: 1,
  });
  const email = localStorage.getItem("email");
  const ownerId = localStorage.getItem("ownerId");
  const navigate = useNavigate();

  useEffect(() => {
    getAllUsers(setLoader, setUser, email);
  }, []);

  useEffect(() => {
    if (user && user.length > 0) {
      handleAdd();
      const currentUser = user.find((u) => u.email === email);
      if (currentUser) {
        if (currentUser.id) {
          localStorage.setItem("ownerId", currentUser.id);
        } else {
          const newId = v4();
          localStorage.setItem("ownerId", newId);
          const db = getDatabase();
          get(ref(db, "userInfo")).then((snap) => {
            if (!snap.exists()) return;
            const allData = snap.val();
            const key = Object.keys(allData).find((k) => allData[k].email === email);
            if (key) update(ref(db, `userInfo/${key}`), { id: newId });
          });
        }
      }
    }
  }, [user]);

  useEffect(() => {
    if (!ownerId) return;
    const db = getDatabase();
    get(ref(db, `tables/${ownerId}`)).then((snap) => {
      const count = snap.exists()
        ? Object.values(snap.val()).filter((t) => !t.isDeleted).length
        : 0;
      setSetupStatus((s) => ({ ...s, tables: count }));
    }).catch(() => setSetupStatus((s) => ({ ...s, tables: 0 })));

    get(ref(db, `waiters/${ownerId}`)).then((snap) => {
      const count = snap.exists() ? Object.values(snap.val()).length : 0;
      setSetupStatus((s) => ({ ...s, staff: count }));
    }).catch(() => setSetupStatus((s) => ({ ...s, staff: 0 })));

    get(ref(db, `menu/${ownerId}`)).then((snap) => {
      const done = snap.exists() && !!snap.val().restaurantName ? 1 : 0;
      setSetupStatus((s) => ({ ...s, menu: done }));
    }).catch(() => setSetupStatus((s) => ({ ...s, menu: 0 })));
  }, [ownerId]);

  async function handleAdd() {
    const filteredData = user.filter((i) => i.email === email);
    if (filteredData.length === 0) {
      addUser(setLoader, setUser, email);
    }
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="px-5 pt-6 pb-2">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your business settings</p>
      </div>

      <div className="mx-5 mt-4 flex flex-col gap-y-3">

        {/* ── Setup progress ── */}
        {(() => {
          const checkable = CONFIG_ITEMS.filter((i) => !i.alwaysDone);
          const doneCount = checkable.filter((i) => setupStatus[i.key] > 0).length;
          const allDone = doneCount === checkable.length;
          return (
            <div className="mb-1">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Setup checklist
                </p>
                <p className={`text-xs font-semibold ${allDone ? "text-green-500" : "text-amber-500"}`}>
                  {doneCount} / {checkable.length} done
                </p>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${allDone ? "bg-green-400" : "bg-amber-400"}`}
                  style={{ width: `${(doneCount / checkable.length) * 100}%` }}
                />
              </div>
            </div>
          );
        })()}

        {/* ── Config cards ── */}
        {CONFIG_ITEMS.map((item) => {
          const count = setupStatus[item.key];
          const isDone = item.alwaysDone || count > 0;
          const isLoading = count === null && !item.alwaysDone;
          return (
            <button
              key={item.to}
              onClick={() => navigate(item.to)}
              className={`w-full flex items-center gap-4 rounded-2xl shadow-card px-4 py-4 border text-left transition-colors ${
                isDone ? "bg-white border-gray-100" : "bg-amber-50 border-amber-200"
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xl ${
                isDone ? "bg-gray-50" : "bg-amber-100"
              }`}>
                {item.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{item.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                {!isLoading && (
                  <span className={`inline-flex items-center gap-1 mt-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
                    isDone ? "bg-green-50 text-green-600" : "bg-amber-100 text-amber-700"
                  }`}>
                    {isDone ? (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                        {item.configuredLabel(count)}
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
                        </svg>
                        Not set up yet
                      </>
                    )}
                  </span>
                )}
              </div>
              <div className={`flex-shrink-0 text-xs font-semibold flex items-center gap-0.5 ${
                isDone ? "text-gray-400" : "text-amber-600"
              }`}>
                {isDone ? "Edit" : "Set up"}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          );
        })}

        <div className="mb-4" />
      </div>

      {loading && <Loader loading={loading} />}
    </div>
  );
}

export default Settings;
